import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { SquareMinusIcon, SquarePlusIcon, Trash2Icon } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';

import {
  ScrollableCellWithAvatar,
  ScrollableCellWithColorDot,
} from '@/components/data-table/cells';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import i18n from '@/core/i18n';
import { formatTrimmedDecimal } from '@/helpers/finance';
import { cn } from '@/lib/utils';
import { InputFieldWithCurrency, SingleSelect } from '@/modules/common';
import { SingleSelectWithPagination } from '@/modules/common/components/molecules/selectors/single-select/single-select-with-pagination';
import {
  createScrollableOptionRenderer,
  createScrollableTriggerRenderer,
  ExpandAllRowsButton,
  QuickBooksWarningsIndicator,
} from '@/modules/finance/common';
import {
  FINANCE_MAX_INTEGER_DIGITS,
  FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES,
} from '@/modules/finance/common/constants/rounding.constants';
import { formatDisplayDate } from '@/utils/date-formatters';

import {
  createBillFormFieldAccessor,
  DISABLED_ROW_CLASS,
  getBillFieldName,
  hasMemberQuickBooksError,
  hasProjectQuickBooksError,
  hasSelectableSubRows,
  isAddedProjectSubRow,
  isAddOverheadSubRow,
  isOverheadSubRow,
  isProjectSubRow,
  isReadOnly,
  isRowDisabled,
  isSalaryAdjustmentSubRow,
  isSubRow,
  isTimeOffSubRow,
} from '../components/generate-bills-table/bill-row.helpers';
import { AddCustomLineButton } from '../components/generate-bills-table/cells/add-custom-line-button';
import {
  OverheadTotal,
  ProjectTotal,
} from '../components/generate-bills-table/cells/line-total-cells';
import {
  MemberAverageRate,
  MemberTotalAmount,
  MemberTotalHours,
} from '../components/generate-bills-table/cells/member-summary-cells';
import { MonthlySalarySwitch } from '../components/generate-bills-table/cells/monthly-salary-switch';
import {
  OverheadDescriptionInput,
  ProjectHoursInput,
  ProjectRateCell,
} from '../components/generate-bills-table/cells/project-input-cells';
import {
  SalaryAdjustmentLabel,
  SalaryAdjustmentTotal,
  SalaryOnlyBillIndicator,
} from '../components/generate-bills-table/cells/salary-adjustment-cells';
import {
  MemberZeroAmountWarning,
  ProjectZeroAmountError,
} from '../components/generate-bills-table/cells/validation-warning-cells';
import {
  AddedProjectActions,
  AddedProjectSelector,
  MonthlySalaryActions,
  OverheadActions,
} from '../components/generate-bills-table/generate-bills-table.types';
import {
  BillHoursSource,
  BillHoursSourceType,
  BillTimeOffSource,
  BillTimeOffSourceType,
  GenerateBillAddedProjectListItem,
  GenerateBillListItem,
  GenerateBillMemberOverheadListItem,
  GenerateBillMemberProjectListItem,
  GenerateBillMemberTimeOffListItem,
} from '../types';
import { QuickBooksMemberWarningTypeDTO } from '../types/dto/response/quick-books-member-warning.response';
import { GenerateBillsTableFormValues } from '../validations';
import { TIME_OFF_SOURCE_LABEL_KEYS } from './time-off-source-label-keys';

const columnHelper = createColumnHelper<GenerateBillListItem>();

const renderProjectOption = createScrollableOptionRenderer({ showColorDot: true });
const renderProjectTrigger = createScrollableTriggerRenderer({ showColorDot: true });

const ADDED_PROJECT_SELECTOR_QUERY_KEY = ['generate-bills-added-project-options'];

/** Display order for the time-off approval-status picker. */
const TIME_OFF_SOURCE_ORDER = [
  BillTimeOffSourceType.APPROVED,
  BillTimeOffSourceType.PENDING,
  BillTimeOffSourceType.APPROVED_AND_PENDING,
] as const;

export interface GenerateBillsTableColumnsParams {
  form: UseFormReturn<GenerateBillsTableFormValues>;
  overheadActions: OverheadActions;
  addedProjectActions: AddedProjectActions;
  addedProjectSelector: AddedProjectSelector;
  monthlySalaryActions: MonthlySalaryActions;
}

const buildMemberQuickBooksWarningMessages = (member: GenerateBillListItem): string[] =>
  member.warnings.map(warning => {
    const args =
      warning.type === QuickBooksMemberWarningTypeDTO.MEMBER_CURRENCY_MISMATCHES_QUICK_BOOKS &&
      warning.args
        ? {
            ...warning.args,
            endOfRequestedPeriod: formatDisplayDate(String(warning.args.endOfRequestedPeriod)),
          }
        : warning.args;
    return i18n.t(
      `authenticated.bills:modals.quickbooksWarning.member.warnings.${warning.type}`,
      args
    );
  });

const buildProjectQuickBooksWarningMessages = (
  project: GenerateBillMemberProjectListItem
): string[] =>
  project.warnings.map(warning =>
    i18n.t(`authenticated.bills:modals.quickbooksWarning.project.warnings.${warning.type}`)
  );

export const getGenerateBillsTableColumns = ({
  form,
  overheadActions,
  addedProjectActions,
  addedProjectSelector,
  monthlySalaryActions,
}: GenerateBillsTableColumnsParams): ColumnDef<GenerateBillListItem>[] => {
  const fields = createBillFormFieldAccessor(form);

  return [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <label className="flex cursor-pointer items-center justify-center px-3 py-3">
          <Checkbox
            checked={
              table.getIsAllRowsSelected()
                ? true
                : table.getIsSomeRowsSelected()
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={value => {
              table.getCoreRowModel().rows.forEach(row => {
                if (table.getIsSomeRowsSelected()) {
                  row.toggleSelected(false);
                  row.subRows.forEach(subRow => subRow.toggleSelected(false));
                  return;
                }
                if (!hasMemberQuickBooksError(row)) {
                  // Select only sub-rows (projects) that have QuickBooks integration
                  row.subRows.forEach(subRow => {
                    if (isSalaryAdjustmentSubRow(subRow)) return;
                    if (!hasProjectQuickBooksError(subRow)) {
                      subRow.toggleSelected(!!value);
                    }
                  });
                }
              });
            }}
            aria-label={i18n.t('authenticated.bills:new.selectAll')}
          />
        </label>
      ),
      cell: ({ row }) => {
        if (isAddOverheadSubRow(row)) return null;

        // The salary adjustment is derived, not chosen: it is always part of the bill while
        // salary mode is on, so it gets an AUTO marker rather than a selectable checkbox.
        if (isSalaryAdjustmentSubRow(row)) {
          return (
            <div className="flex items-center justify-center px-1 py-4">
              <SimpleTooltip content={i18n.t('authenticated.bills:monthlySalary.autoBadgeTooltip')}>
                <span className="cursor-help rounded-sm bg-muted px-1 py-px text-[9px] font-semibold leading-tight tracking-tight text-muted-foreground">
                  {i18n.t('authenticated.bills:monthlySalary.autoBadge')}
                </span>
              </SimpleTooltip>
            </div>
          );
        }

        const disabled = isRowDisabled(row);

        return (
          <label
            onClick={event => event.stopPropagation()}
            className={cn(
              'flex cursor-pointer items-center justify-center py-4',
              disabled && DISABLED_ROW_CLASS
            )}
          >
            {isSubRow(row) ? (
              <Checkbox
                checked={row.getIsSelected()}
                disabled={
                  !row.getCanSelect() ||
                  hasMemberQuickBooksError(row.getParentRow()) ||
                  hasProjectQuickBooksError(row)
                }
                onCheckedChange={value => row.toggleSelected(!!value)}
                aria-label={i18n.t('authenticated.bills:new.selectRow')}
              />
            ) : (
              <Checkbox
                checked={
                  row.getIsAllSubRowsSelected()
                    ? true
                    : row.getIsSomeSelected()
                      ? 'indeterminate'
                      : false
                }
                disabled={
                  !row.getCanSelect() || hasMemberQuickBooksError(row) || !hasSelectableSubRows(row)
                }
                className="w-4"
                onCheckedChange={value => {
                  row.subRows.forEach(subRow => {
                    if (isSalaryAdjustmentSubRow(subRow)) return;
                    if (row.getIsSomeSelected()) {
                      subRow.toggleSelected(false);
                      return;
                    }
                    if (!hasProjectQuickBooksError(subRow)) {
                      subRow.toggleSelected(!!value);
                    }
                  });
                }}
                aria-label={i18n.t('authenticated.bills:new.selectRow')}
              />
            )}
          </label>
        );
      },
      size: 40,
      minSize: 40,
      maxSize: 40,
      enableSorting: false,
      enableColumnFilter: false,
      enablePinning: true,
    }),
    columnHelper.accessor('member.name', {
      id: 'memberAndProject',
      header: ({ table }) => (
        <span className="flex items-center gap-1">
          <ExpandAllRowsButton table={table} />
          {i18n.t('authenticated.bills:new.headers.memberAndProject')}
        </span>
      ),
      size: 350,
      minSize: 250,
      meta: {
        cellClassName: 'pl-0',
        title: i18n.t('authenticated.bills:new.headers.memberAndProject'),
      },
      cell: ({ row, getValue }) => {
        const disabled = isRowDisabled(row);

        if (isAddOverheadSubRow(row)) {
          if (hasMemberQuickBooksError(row.getParentRow())) return null;
          return (
            <AddCustomLineButton
              row={row}
              overheadActions={overheadActions}
              addedProjectActions={addedProjectActions}
            />
          );
        }

        if (isAddedProjectSubRow(row)) {
          const addedProject = row.original as unknown as GenerateBillAddedProjectListItem;
          const parentRow = row.getParentRow();

          // Deliberately NOT excluding already-added projects: a member may legitimately be
          // billed against the same project several times at different rates. Rows are keyed
          // by their own UUID, so duplicates get distinct row ids and form paths.
          return (
            <div className="flex min-w-0 items-center gap-2 pl-10 pt-1">
              <div className="min-w-0 flex-1">
                <SingleSelectWithPagination
                  name={getBillFieldName(row, 'projectId')}
                  control={form.control}
                  disabled={isReadOnly(row)}
                  showAvatars
                  avatarShape="rounded"
                  queryKey={ADDED_PROJECT_SELECTOR_QUERY_KEY}
                  loadItems={addedProjectSelector.loadItems}
                  searchItems={addedProjectSelector.searchItems}
                  renderOption={renderProjectOption}
                  renderTrigger={renderProjectTrigger}
                  placeholder={i18n.t('authenticated.bills:lineItemActions.selectProject')}
                  isClearSelectionEnabled={false}
                />
              </div>
              <Button
                variant="ghost"
                size="xxs"
                className="shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() =>
                  parentRow &&
                  addedProjectActions.removeAddedProject(parentRow.original.id, addedProject.id)
                }
              >
                <Trash2Icon size={14} />
              </Button>
            </div>
          );
        }

        if (isOverheadSubRow(row)) {
          const overhead = row.original as unknown as GenerateBillMemberOverheadListItem;
          const parentRow = row.getParentRow();
          return (
            <div className="flex min-w-0 items-center gap-2 pl-10 pt-1">
              <OverheadDescriptionInput row={row} form={form} defaultValue={overhead.description} />
              <Button
                variant="ghost"
                size="xxs"
                className="shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() =>
                  parentRow && overheadActions.removeOverhead(parentRow.original.id, overhead.id)
                }
              >
                <Trash2Icon size={14} />
              </Button>
            </div>
          );
        }

        if (isSalaryAdjustmentSubRow(row)) {
          return <SalaryAdjustmentLabel row={row} />;
        }

        if (isTimeOffSubRow(row)) {
          const original = row.original as unknown as GenerateBillMemberTimeOffListItem;
          return (
            <div className="flex min-w-0 items-center gap-3 pl-10 pt-1">
              <div
                className={cn(
                  'flex min-w-0 flex-1 items-center gap-3',
                  disabled && DISABLED_ROW_CLASS
                )}
              >
                <span className="truncate text-sm">{original.timeOffTypeName}</span>
                <ProjectZeroAmountError row={row} />
              </div>
            </div>
          );
        }

        if (isProjectSubRow(row)) {
          const original = row.original as unknown as GenerateBillMemberProjectListItem;
          const { name, clientName, logoUrl, colorHex } = original;
          const displayName = clientName ? `${name} (${clientName})` : name;

          return (
            <div className="flex min-w-0 items-center gap-3 pl-10 pt-1">
              <div
                className={cn(
                  'flex min-w-0 flex-1 items-center gap-3',
                  disabled && DISABLED_ROW_CLASS
                )}
              >
                <ScrollableCellWithColorDot
                  text={displayName}
                  image={logoUrl}
                  avatarName={name}
                  alt={`${name} logo`}
                  dotColor={colorHex || 'hsl(var(--muted-foreground))'}
                  className="min-w-0 flex-1"
                />
                <ProjectZeroAmountError row={row} />
              </div>
              <QuickBooksWarningsIndicator
                messages={buildProjectQuickBooksWarningMessages(original)}
              />
            </div>
          );
        }
        const canExpand = row.subRows?.length > 0;

        return (
          <div className="flex min-w-0 items-center">
            <div className="flex min-w-0 flex-1 items-center">
              <Button
                variant="ghost"
                onClick={row.getToggleExpandedHandler()}
                style={{
                  cursor: 'pointer',
                  border: 'none',
                  background: 'none',
                  visibility: canExpand ? 'visible' : 'hidden',
                }}
                size="xxs"
                className={cn('mx-3 shrink-0 text-muted-foreground')}
              >
                {row.getIsExpanded() ? <SquareMinusIcon size={16} /> : <SquarePlusIcon size={16} />}
              </Button>
              <ScrollableCellWithAvatar
                text={getValue()}
                image={row.original.member?.avatarUrl}
                avatarName={getValue()}
                alt={`${getValue()} avatar`}
                className={cn('min-w-0 flex-1', disabled && DISABLED_ROW_CLASS)}
              />
              <SalaryOnlyBillIndicator row={row} />
              <MemberZeroAmountWarning row={row} />
            </div>
            <QuickBooksWarningsIndicator
              messages={buildMemberQuickBooksWarningMessages(row.original)}
            />
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'monthlySalary',
      header: i18n.t('authenticated.bills:new.headers.monthlySalary'),
      size: 200,
      minSize: 180,
      enableSorting: false,
      cell: ({ row }) => {
        // Billing mode is a member-level decision — sub-rows inherit it.
        if (isSubRow(row)) return null;

        return <MonthlySalarySwitch row={row} monthlySalaryActions={monthlySalaryActions} />;
      },
    }),
    columnHelper.display({
      id: 'billBy',
      header: i18n.t('authenticated.bills:new.headers.hoursSource'),
      size: 200,
      minSize: 200,
      cell: ({ row }) => {
        if (isAddOverheadSubRow(row)) return null;

        const disabled = isRowDisabled(row);

        if (isSalaryAdjustmentSubRow(row)) {
          return (
            <span className="text-sm text-muted-foreground">
              {i18n.t('authenticated.bills:monthlySalary.adjustmentSourceLabel')}
            </span>
          );
        }

        if (isOverheadSubRow(row) || isAddedProjectSubRow(row)) {
          return (
            <span className="text-sm text-muted-foreground">{i18n.t('common:table.noValue')}</span>
          );
        }

        if (isTimeOffSubRow(row)) {
          const original = row.original as unknown as GenerateBillMemberTimeOffListItem;

          const currentBillBy = fields.getFieldValue(getBillFieldName(row, 'billBy'));
          if (!currentBillBy) {
            fields.setFieldValue(getBillFieldName(row, 'billBy'), original.billBy, {
              shouldValidate: false,
              shouldDirty: false,
            });
            fields.setFieldValue(getBillFieldName(row, 'hours'), original.hours, {
              shouldValidate: false,
              shouldDirty: false,
            });
            fields.setFieldValue(getBillFieldName(row, 'effectiveRate'), original.effectiveRate, {
              shouldValidate: false,
              shouldDirty: false,
            });
            fields.setFieldValue(getBillFieldName(row, 'total'), original.total, {
              shouldValidate: false,
              shouldDirty: false,
            });
          }

          const orderedSources = TIME_OFF_SOURCE_ORDER.map(sourceType =>
            original.hoursSources.find(s => s.sourceType === sourceType)
          ).filter((source): source is BillTimeOffSource => source !== undefined);

          // Nothing to choose when the member has hours in only one approval status — show the
          // single status as a disabled picker rather than an interactive one.
          const hasChoice = orderedSources.length > 1;

          const options = orderedSources.map(source => ({
            label: i18n.t(TIME_OFF_SOURCE_LABEL_KEYS[source.sourceType]),
            value: source.sourceType,
            customContent: (
              <div className="flex w-full items-center justify-between gap-3">
                <span>{i18n.t(TIME_OFF_SOURCE_LABEL_KEYS[source.sourceType])}</span>
                <span>{i18n.t('common:timeFormat.hoursOnly', { hours: source.hours })}</span>
              </div>
            ),
          }));

          return (
            <div className={cn(disabled && DISABLED_ROW_CLASS)}>
              <SingleSelect
                options={options}
                name={getBillFieldName(row, 'billBy')}
                control={form.control}
                disabled={isReadOnly(row) || !hasChoice}
                searchable={false}
                isClearSelectionEnabled={false}
                onChange={(value: BillTimeOffSourceType) => {
                  const selectedSource = original.hoursSources.find(s => s.sourceType === value);
                  if (!selectedSource) return;

                  fields.setFieldValue(getBillFieldName(row, 'hours'), selectedSource.hours, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  fields.setFieldValue(
                    getBillFieldName(row, 'effectiveRate'),
                    selectedSource.averageRate,
                    { shouldValidate: true, shouldDirty: true }
                  );
                  fields.setFieldValue(getBillFieldName(row, 'total'), selectedSource.amount, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                renderTrigger={selectedOption => {
                  if (selectedOption) {
                    return (
                      <div className="flex w-full items-center justify-start gap-2">
                        {selectedOption.label}
                      </div>
                    );
                  }
                }}
              />
            </div>
          );
        }

        if (isProjectSubRow(row)) {
          const original = row.original as unknown as GenerateBillMemberProjectListItem;

          const trackedSource = original.hoursSources.find(
            s => s.sourceType === BillHoursSourceType.TRACKED
          );

          const currentBillBy = fields.getFieldValue(getBillFieldName(row, 'billBy'));
          if (!currentBillBy && trackedSource) {
            fields.setFieldValue(getBillFieldName(row, 'billBy'), trackedSource.sourceType, {
              shouldValidate: false,
              shouldDirty: false,
            });
            fields.setFieldValue(getBillFieldName(row, 'hours'), trackedSource.hours, {
              shouldValidate: false,
              shouldDirty: false,
            });
            fields.setFieldValue(
              getBillFieldName(row, 'effectiveRate'),
              trackedSource.averageRate,
              { shouldValidate: false, shouldDirty: false }
            );
            fields.setFieldValue(getBillFieldName(row, 'total'), trackedSource.amount, {
              shouldValidate: false,
              shouldDirty: false,
            });
          }

          const sourceOrder = [
            BillHoursSourceType.TRACKED,
            BillHoursSourceType.BOOKED,
            BillHoursSourceType.CAPACITY,
            BillHoursSourceType.APPROVED,
          ];
          const orderedSources = sourceOrder
            .map(sourceType => original.hoursSources.find(s => s.sourceType === sourceType))
            .filter((source): source is BillHoursSource => source !== undefined);

          const options = orderedSources.map(source => {
            const labelKey = source.sourceType.toLowerCase();

            return {
              label: i18n.t(`authenticated.bills:new.hoursSource.${labelKey}`),
              value: source.sourceType,
              customContent: (
                <div className="flex w-full items-center justify-between">
                  <span>{i18n.t(`authenticated.bills:new.hoursSource.${labelKey}`)}</span>
                  <span>{i18n.t('common:timeFormat.hoursOnly', { hours: source.hours })}</span>
                </div>
              ),
            };
          });

          return (
            <div className={cn(disabled && DISABLED_ROW_CLASS)}>
              <SingleSelect
                options={options}
                name={getBillFieldName(row, 'billBy')}
                control={form.control}
                disabled={isReadOnly(row)}
                searchable={false}
                isClearSelectionEnabled={false}
                onChange={(value: BillHoursSourceType) => {
                  const selectedSource = original.hoursSources.find(s => s.sourceType === value);

                  if (selectedSource) {
                    const writeOptions = { shouldValidate: true, shouldDirty: true };
                    fields.setFieldValue(
                      getBillFieldName(row, 'hours'),
                      selectedSource.hours,
                      writeOptions
                    );
                    fields.setFieldValue(
                      getBillFieldName(row, 'effectiveRate'),
                      selectedSource.averageRate,
                      writeOptions
                    );
                    fields.setFieldValue(
                      getBillFieldName(row, 'total'),
                      selectedSource.amount,
                      writeOptions
                    );
                  }
                }}
                renderTrigger={selectedOption => {
                  if (selectedOption) {
                    return (
                      <div className="flex w-full items-center justify-start gap-2">
                        {selectedOption.label}
                      </div>
                    );
                  }
                }}
              />
            </div>
          );
        }

        return (
          <span className={cn(disabled && DISABLED_ROW_CLASS)}>
            {i18n.t('common:table.noValue')}
          </span>
        );
      },
    }),
    columnHelper.accessor('hours', {
      header: i18n.t('authenticated.bills:new.headers.totalHours'),
      size: 150,
      minSize: 125,
      cell: ({ row }) => {
        if (isAddOverheadSubRow(row)) return null;

        if (isSalaryAdjustmentSubRow(row)) {
          return (
            <span className="text-sm text-muted-foreground">{i18n.t('common:table.noValue')}</span>
          );
        }

        if (isOverheadSubRow(row)) {
          return <span className="text-sm text-muted-foreground">{formatTrimmedDecimal(1)}</span>;
        }

        const disabled = isRowDisabled(row);

        if (isSubRow(row)) {
          return (
            <div className={cn(disabled && DISABLED_ROW_CLASS)}>
              <ProjectHoursInput row={row} form={form} />
            </div>
          );
        }

        return (
          <div className={cn(disabled && DISABLED_ROW_CLASS)}>
            <MemberTotalHours row={row} />
          </div>
        );
      },
    }),
    columnHelper.accessor('effectiveRate', {
      header: i18n.t('authenticated.bills:new.headers.effectiveRate'),
      size: 250,
      minSize: 200,
      cell: ({ row }) => {
        if (isAddOverheadSubRow(row)) return null;

        if (isSalaryAdjustmentSubRow(row)) {
          return (
            <span className="text-sm text-muted-foreground">{i18n.t('common:table.noValue')}</span>
          );
        }

        if (isOverheadSubRow(row)) {
          return (
            <InputFieldWithCurrency
              name={getBillFieldName(row, 'effectiveRate')}
              isReadMode={!row.getIsSelected()}
              control={form.control}
              suffixText={
                (row.original as unknown as GenerateBillMemberOverheadListItem).currency.code
              }
              maxDecimalPlaces={FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES}
              maxIntegerDigits={FINANCE_MAX_INTEGER_DIGITS}
              allowNegative
            />
          );
        }

        const disabled = isRowDisabled(row);

        if (isSubRow(row)) {
          return (
            <div className={cn(disabled && DISABLED_ROW_CLASS)}>
              <ProjectRateCell row={row} form={form} />
            </div>
          );
        }

        return (
          <div className={cn(disabled && DISABLED_ROW_CLASS)}>
            <MemberAverageRate row={row} currencyCode={row.original.currencyCode} />
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'total',
      header: () => i18n.t('authenticated.bills:new.headers.billTotal'),
      size: 175,
      minSize: 125,
      cell: ({ row }) => {
        if (isAddOverheadSubRow(row)) return null;

        if (isSalaryAdjustmentSubRow(row)) {
          return <SalaryAdjustmentTotal row={row} />;
        }

        if (isOverheadSubRow(row)) {
          return <OverheadTotal row={row} />;
        }

        const disabled = isRowDisabled(row);

        if (isAddedProjectSubRow(row)) {
          return (
            <div className={cn(disabled && DISABLED_ROW_CLASS)}>
              <ProjectTotal
                row={row}
                currencyCode={
                  (row.original as unknown as GenerateBillAddedProjectListItem).currency.code
                }
              />
            </div>
          );
        }

        if (isSubRow(row)) {
          return (
            <div className={cn(disabled && DISABLED_ROW_CLASS)}>
              <ProjectTotal row={row} currencyCode={row.original.currency.code} />
            </div>
          );
        }

        return (
          <div className={cn(disabled && DISABLED_ROW_CLASS)}>
            <MemberTotalAmount row={row} currencyCode={row.original.currencyCode} />
          </div>
        );
      },
    }),
  ];
};
